import {
  ChangeDetectionStrategy,
  Component,
  computed,
  inject,
  input,
  Signal,
} from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { MatIconButton } from '@angular/material/button';
import { MatRipple } from '@angular/material/core';
import { MatIcon } from '@angular/material/icon';
import { MatTooltip } from '@angular/material/tooltip';
import { OrganisationService } from '@principle-theorem/ng-principle-shared';
import { NgCommonModule } from '@principle-theorem/ng-shared';
import { ReportingPermissions } from '@principle-theorem/principle-core/features';
import {
  ICustomReport,
  ICustomReportFilter,
} from '@principle-theorem/principle-core/interfaces';
import {
  CustomReportFilter,
  ReportBuilderDataSourceInstance,
} from '@principle-theorem/reporting';
import { ICustomReportFilterSummary } from '@principle-theorem/reporting/interfaces';
import { Firestore, firstValueFrom, WithRef } from '@principle-theorem/shared';
import { ReportBuilderStore } from '../../../report-builder/report-builder.store';
import { ReportEditorService } from '../../../report-builder/report-editor.service';
import { IEditReportFilterDialogResponse } from '../edit-report-filter-dialog/edit-report-filter-dialog.component';
import { CustomReportFilterComponent } from './custom-report-filter/custom-report-filter.component';

@Component({
  selector: 'pr-custom-report-filters',
  templateUrl: './custom-report-filters.component.html',
  styleUrls: ['./custom-report-filters.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    CustomReportFilterComponent,
    MatIcon,
    MatIconButton,
    MatRipple,
    MatTooltip,
    NgCommonModule,
  ],
})
export class CustomReportFiltersComponent {
  private _organisation = inject(OrganisationService);
  private _reportEditor = inject(ReportEditorService);
  store = inject(ReportBuilderStore);
  hasEditPermissions: Signal<boolean>;
  hasNoFilters: Signal<boolean>;

  customReport = input.required<WithRef<ICustomReport>>();
  readonly = input(false);

  constructor() {
    this.hasEditPermissions = toSignal(
      this._organisation.userHasPermission$(
        ReportingPermissions.ReportingCustomReportEdit
      ),
      {
        initialValue: false,
      }
    );

    const dynamicFilters = toSignal(this.store.dynamicFilters$, {
      initialValue: [],
    });

    this.hasNoFilters = computed(
      () => ![this.customReport().staticFilters, dynamicFilters()].flat().length
    );
  }

  getFilterSummary(data: {
    filter: ICustomReportFilter;
    dataSource: ReportBuilderDataSourceInstance;
  }): ICustomReportFilterSummary | undefined {
    const factTable = data.dataSource?.factTable;
    if (!factTable) {
      return;
    }
    return CustomReportFilter.getSummarySegments(factTable, data.filter);
  }

  async addFilter(): Promise<void> {
    const dataSource = await firstValueFrom(this.store.dataSource$);
    if (!dataSource) {
      return;
    }

    const response = await this._reportEditor.editFilter(dataSource);
    if (!response) {
      return;
    }

    const customReport = this.customReport();
    const staticFilters = [...customReport.staticFilters, response.filter];

    await Firestore.patchDoc(customReport.ref, { staticFilters });
    this.store.setCurrentReport({ ...customReport, staticFilters });
    await this.store.reloadReport({ staticFilters });
  }

  async editFilter(filter?: ICustomReportFilter): Promise<void> {
    const dataSource = await firstValueFrom(this.store.dataSource$);
    if (!dataSource) {
      return;
    }

    const response = await this._reportEditor.editFilter(dataSource, filter);
    if (!response) {
      return;
    }

    const customReport = this.customReport();
    const staticFilters = this._getFilterUpdates(customReport, response);
    await Firestore.patchDoc(customReport.ref, { staticFilters });
    this.store.setCurrentReport({ ...customReport, staticFilters });
    await this.store.reloadReport({ staticFilters });
  }

  async deleteFilter(filter: ICustomReportFilter): Promise<void> {
    const customReport = this.customReport();
    const staticFilters = this._getFilterUpdates(customReport, {
      deleteFilter: true,
      filter,
    });

    await Firestore.patchDoc(customReport.ref, { staticFilters });
    this.store.setCurrentReport({ ...customReport, staticFilters });
    await this.store.reloadReport({ staticFilters });
  }

  async saveFilter(filter: ICustomReportFilter): Promise<void> {
    const customReport = this.customReport();
    const staticFilters = [...customReport.staticFilters, filter];
    await Firestore.patchDoc(customReport.ref, { staticFilters });
    this.store.setCurrentReport({ ...customReport, staticFilters });
    this.resetFilter(filter);
    await this.store.reloadReport({ staticFilters });
  }

  resetFilter(filter: ICustomReportFilter): void {
    this.store.resetFilter$.next({ dimensionId: filter.id });
  }

  private _getFilterUpdates(
    customReport: WithRef<ICustomReport>,
    response: IEditReportFilterDialogResponse
  ): ICustomReportFilter[] {
    if (response.deleteFilter) {
      return customReport.staticFilters.filter(
        (filter) => filter.uid !== response.filter.uid
      );
    }

    return customReport.staticFilters.map((filter) =>
      filter.uid === response.filter.uid ? response.filter : filter
    );
  }
}
